import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteMethod,
  getMethod,
  postMethod2,
} from "../../services/ApiCallMethods";

// POST: Upload client document
export const uploadClientDocument = createAsyncThunk(
  "document/uploadClientDocument",
  async (formDataPayload, { rejectWithValue }) => {
    try {
      const response = await postMethod2(
        `/api/documents/upload`,
        formDataPayload
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Upload failed");
      }

      return data;
    } catch (error) {
      console.error("Error uploading document:", error);
      return rejectWithValue(error?.message || "Upload failed");
    }
  }
);

export const getClientDocuments = createAsyncThunk(
  "document/getClientDocuments",
  async (
    {
      clientId,
      currentPage = 1,
      itemsPerPage = 10,
      search = "",
      accountType = "",
      account = "",
      ownerEntity = "",
      timeFrame = "",
      categories,
      startDate = "",
      endDate = "",
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();

      if (search) params.append("search", search);
      if (clientId) params.append("clientId", clientId);
      if (accountType) params.append("account_type", accountType);
      if (account) params.append("account", account);
      if (ownerEntity) params.append("ownerEntity", ownerEntity)
      if (timeFrame) params.append("timeframe", timeFrame);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (Array.isArray(categories) && categories.length > 0) {
        params.append("categoryId", categories.join(","));
      }
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);
      const response = await getMethod(
        `/api/getAllDocuments?${params.toString()}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching client documents:", error);
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);


export const getDocumentClients = createAsyncThunk(
  "document/getDocumentsClients",
  async ({ advisorId, clientId, docId, search, page }, { rejectWithValue }) => {
    try {
      const { currentPage, itemsPerPage } = page || {};
      const queryParams = new URLSearchParams();
      if (search) queryParams.append("search", search);
      if (currentPage) queryParams.append("page", currentPage.toString());
      if (itemsPerPage) queryParams.append("limit", itemsPerPage.toString());
      if (advisorId) queryParams.append("advisorId", advisorId.toString());
      if (clientId) queryParams.append("clientId", clientId.toString());
      const url = `/api/documents/getClientByDocumentId/${docId}?${queryParams.toString()}`;

      const response = await getMethod(url);
      const data = await response.json();

      return data;
    } catch (error) {
      console.error("Error fetching client documents:", error);
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);

export const getDocumentProperties = createAsyncThunk(
  "document/getDocumentsProperties",
  async ({ advisorId, clientId, docId, search, page }, { rejectWithValue }) => {
    try {
      const { currentPage, itemsPerPage } = page;
      const queryParams = new URLSearchParams();

      if (search) queryParams.append("search", search);
      if (currentPage) queryParams.append("page", currentPage);
      if (itemsPerPage) queryParams.append("limit", itemsPerPage);
      if (advisorId) queryParams.append("advisorId", advisorId);
      if (clientId) queryParams.append("clientId", clientId);

      const url = `/api/documents/getPropertyByDocumentId/${docId}?${queryParams.toString()}`;

      const response = await getMethod(url);
      const data = await response.json();

      return data;
    } catch (error) {
      console.error("Error fetching property documents:", error);
      return rejectWithValue(error?.message || "Failed to fetch property documents");
    }
  }
);

export const getClientSearchDocument = createAsyncThunk(
  "document/getClientSearchDocuments",
  async (
    { clientId, currentPage, itemsPerPage, ownerEntity, searchItem },
    { rejectWithValue }
  ) => {
    try {
      const response = await getMethod(
        `/api/documents/${444}?page=${currentPage}&limit=${itemsPerPage}&search=${searchItem}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching client documents:", error);
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);

export const getProperties = createAsyncThunk(
  "document/getProperties",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/properties?page=1&limit=10`);
      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error("Error fetching client documents:", error);
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);

export const getClients = createAsyncThunk(
  "document/getClients",
  async (propertyId, { rejectWithValue }) => {
    try {

      if (propertyId) {
        const response = await getMethod(`/api/${propertyId}/clients`);
        const data = await response.json();
        return data.data || data;
      } else {
        const response = await getMethod(`/api/clients?page=1&limit=4000`);
        const data = await response.json();
        return data.data || data;
      }
    } catch (error) {
      console.error("Error fetching client documents:", error);
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);

// export const getSecurities = createAsyncThunk(
//   "profileSummary/getSecurities",
//   async (id, { rejectWithValue }) => {
//     try {
//       const response = await getMethod(
//         `/profileSummary/getSecurities/${id ?? 0}`
//       );
//       const data = await response.json();
//       return data.data || data;
//     } catch (error) {
//       console.error("Error fetching securities:", error);
//       return rejectWithValue(error?.message || "Failed to fetch securities");
//     }
//   }
// );


export const getDocumentsByAccount = createAsyncThunk(
  "documents/getDocumentsByAccountType",
  async ({ name, page, limit, categories, adminClientId, advisorId, accountType, accountCategory, ownerEntity, timeFrame, startDate, endDate }, { rejectWithValue }) => {
    try {

      const queryParams = new URLSearchParams();
      // Always inclu  de required parameters

      // Conditionally add optional parameters only if they have values
      if (accountType && String(accountType).trim() !== "") {
        queryParams.append("account_type", encodeURIComponent(accountType));
      }
      if (accountCategory && String(accountCategory).trim() !== "") {
        queryParams.append("account", encodeURIComponent(accountCategory));
      }
      if (timeFrame && timeFrame.trim() !== "") {
        queryParams.append("timeFrame", encodeURIComponent(timeFrame));
      }
      if (advisorId) {
        queryParams.append("advisorId", encodeURIComponent(advisorId));
      }
      if (adminClientId) {
        queryParams.append("clientId", encodeURIComponent(adminClientId));
      }
      if (ownerEntity) {
        queryParams.append("ownerEntity", encodeURIComponent(ownerEntity));
      }
      if (startDate) {
        queryParams.append("startDate", startDate);
      }
      if (endDate) {
        queryParams.append("endDate", endDate);
      }
      if (Array.isArray(categories) && categories.length > 0) {
        queryParams.append("categoryId", categories.join(","));
      }

      queryParams.append("page", page);
      queryParams.append("limit", limit);

      const response = await getMethod(`/api/getAllDocuments?${queryParams}`);

      // const response = await getMethod(
      //   `/api/client/${adminClientId}/documents?${queryParams.toString()}`
      // );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching documents by account:", error);
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);

export const getDocumentsByAccountType = createAsyncThunk(
  "documents/getDocumentsByAccount",
  async (
    { master_id, property_id, encodedownerId, adminClientId, page = 1, limit = 10 },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();

      if (master_id) {
        queryParams.append("account_type", String(master_id));
      }

      if (property_id) {
        queryParams.append("account", String(property_id));
      }

      if (encodedownerId) {
        queryParams.append("ownerEntity", String(encodedownerId));
      }

      if (adminClientId) {
        queryParams.append("clientId", String(adminClientId));
      }

      queryParams.append("page", page);
      queryParams.append("limit", limit);

      const response = await getMethod(
        `/api/getAllDocuments?${queryParams.toString()}`
      );

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching documents by account:", error);
      return rejectWithValue(
        error?.message || "Failed to fetch documents"
      );
    }
  }
);


export const getAllDocuments = createAsyncThunk(
  "document/getAllDocuments",
  async (params, { rejectWithValue }) => {
    try {
      const {
        currentPage,
        itemsPerPage,
        clientId,
        advisorId,
        accountType,
        account,
        ownerEntity,
        categories,
        timeFrame,
        startDate,
        endDate,
      } = params;
      const queryParams = new URLSearchParams();
      if (!clientId) {
        return;
      }
      if (currentPage) queryParams.append("page", currentPage);
      if (itemsPerPage) queryParams.append("limit", itemsPerPage);
      if (clientId) queryParams.append("clientId", clientId);
      if (advisorId) queryParams.append("advisorId", advisorId);
      if (accountType) queryParams.append("account_type", accountType);
      if (account) queryParams.append("account", account);
      if (ownerEntity) queryParams.append("ownerEntity", ownerEntity);
      if (timeFrame) queryParams.append("timeframe", timeFrame);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (Array.isArray(categories) && categories.length > 0) {
        queryParams.append("categoryId", categories.join(","));
      }

      const response = await getMethod(`/api/getAllDocuments?${queryParams}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching documents:", error);
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);
export const getSearchDocuments = createAsyncThunk(
  "document/getSearchDocuments",
  async ({ currentPage, itemsPerPage, searchItem }, { rejectWithValue }) => {
    try {
      const response = await getMethod(
        `/api/getAllDocuments?page=${currentPage}&limit=${itemsPerPage}&search=${searchItem}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching client documents:", error);
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);

export const uploadDocument = createAsyncThunk(
  "document/uploadDocument",
  async (documentPayload, { rejectWithValue }) => {
    try {
      const response = await postMethod2(
        `/api/documents/upload-with-property2
    `,
        documentPayload
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching client documents:", error);
      return rejectWithValue(error?.message || "Failed to fetch documents");
    }
  }
);
// upload document for super admin
export const uploadDocumentForSuperAdmin = createAsyncThunk(
  "api/documents/upload",
  async (documentPayload, { rejectWithValue }) => {
    try {
      const response = await postMethod2(`/api/documents/upload`, documentPayload);
      const data = await response.json();
      return data;
    }
    catch (error) {
      console.error("Error uploading document:", error);
      return rejectWithValue(error?.message || "Failed to upload document");
    }
  }
);

export const deleteDocument = createAsyncThunk(
  "document/deleteDocument",
  async ({ id, property_master_id, investment_client_id }, { rejectWithValue }) => {
    try {
      const response = await deleteMethod(`/api/documents/${id}?clientId=${investment_client_id}&investmentId=${property_master_id}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting client documents:", error);
      return rejectWithValue(error?.message || "Failed to delete documents");
    }
  }
);

export const deleteDocumentDetails = createAsyncThunk(
  "document/deleteDocument",
  async ({ docId, clientId, advisorId, propertyId }, { rejectWithValue }) => {
    try {
      let url = `/api/documents/${docId}`;
      const params = new URLSearchParams();
      if (clientId) params.append("clientId", clientId);
      if (advisorId) params.append("advisorId", advisorId);
      if (propertyId) params.append("propertyId", propertyId);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await deleteMethod(url);
      const data = await response.json();

      return data;
    } catch (error) {
      console.error("Error deleting document details:", error);
      return rejectWithValue(error?.message || "Failed to delete document");
    }
  }
);


export const deleteDocumentClient = createAsyncThunk(
  "document/deleteDocumentClient",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await deleteMethod(
        `/api/documents/removeClientFromDocument/${clientId}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting client documents:", error);
      return rejectWithValue(error?.message || "Failed to delete documents");
    }
  }
);

export const getInvestments = createAsyncThunk(
  "profileSummary/getInvestments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getMethod("/profileSummary/getInvestments");
      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error("Error fetching investments:", error);
      return rejectWithValue(error?.message || "Failed to fetch investments");
    }
  }
);


export const deleteDocumentProperty = createAsyncThunk(
  "document/deleteDocumentProperty",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await deleteMethod(
        `/api/documents/removePropertyMappingFromDocument/${clientId}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting client documents:", error);
      return rejectWithValue(error?.message || "Failed to delete documents");
    }
  }
);

export const getAdvisorProperties = createAsyncThunk(
  "document/getAdvisorProperties",
  async (advisorId, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/properties/${advisorId}`);
      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error("Error fetching advisor properties:", error);
      return rejectWithValue(error?.message || "Failed to fetch properties");
    }
  }
);

export const getAdvisorPropteryClients = createAsyncThunk(
  "document/getAdvisorPropteryClients",
  async ({ advisorId, propertyId }, { rejectWithValue }) => {
    try {
      const response = await getMethod(
        `/api/clientDetail/${advisorId}/${propertyId}`
      );
      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error("Error fetching client detail:", error);
      return rejectWithValue(error?.message || "Failed to fetch client detail");
    }
  }
);

export const getPropertiesofClients = createAsyncThunk(
  "document/getPropertiesofClients",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/client/${clientId}/properties`);
      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error("Error fetching advisor properties:", error);
      return rejectWithValue(error?.message || "Failed to fetch properties");
    }
  }
);



// Initial state
const initialState = {
  loading: false,
  error: null,
  success: false,
  uploadResult: null,
  clientDocuments: [],
  getLoading: false,
  properties: [],
  clients: [],
  allDocuments: [],
  documentUpload: null,
  pagination: {},
  Clientpagination: {},
  clientSecurityPagination: {},
  searchDocuments: [],
  documentClients: {},
  documentProperties: [],
  advisorProperties: [],
  clientDetail: [],
  advisorDocumentProperties: [],
  clientProperties: [],
  securities: [],
  investments: [],
  documents: [],
  docViewPagination: {},
};

// Slice
const documentSlice = createSlice({
  name: "document",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(uploadClientDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(uploadClientDocument.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.documentUpload = action.payload;
      })
      .addCase(uploadClientDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      .addCase(getClientDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.clientDocuments = []; // Clear old data
        state.Clientpagination = {}; // Clear old pagination
      })
      .addCase(getClientDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.clientDocuments = action.payload.data;
        state.Clientpagination = action.payload.pagination;
      })
      .addCase(getClientDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.clientDocuments = []; // Clear old data
        state.Clientpagination = {}; // Clear old pagination
      })
      .addCase(getProperties.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.properties = []; // Clear old data
      })
      .addCase(getProperties.fulfilled, (state, action) => {
        state.loading = false;
        state.properties = action.payload;
      })
      .addCase(getProperties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getClients.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
      })
      .addCase(getClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAllDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.allDocuments = []; // Clear old data
        state.pagination = {}; // Clear old pagination
      })
      .addCase(getAllDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.allDocuments = action.payload?.data || [];
        state.pagination = action.payload?.pagination || {};
      })
      .addCase(getAllDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.loading = false;
        state.documentUpload = action.payload;
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSearchDocuments.pending, (state) => {
        // state.loading = true;
        state.error = null;
        state.success = false;
        state.searchDocuments = []; // Clear old data
        state.pagination = {}; // Clear old pagination
      })
      .addCase(getSearchDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.searchDocuments = action.payload.data || action.payload;
        state.pagination = action.payload.pagination;
      })
      .addCase(getSearchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getClientSearchDocument.pending, (state) => {
        state.error = null;
        state.success = false;
      })
      .addCase(getClientSearchDocument.fulfilled, (state, action) => {
        state.loading = false;
        state.searchDocuments = action.payload.data || action.payload;
        state.pagination = action.payload.pagination;
      })
      .addCase(getClientSearchDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getDocumentClients.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.success = false;
      })
      .addCase(getDocumentClients.fulfilled, (state, action) => {
        state.loading = false;
        state.documentClients = action.payload.clients || [];
        state.pagination = action.payload.pagination || [];
      })
      .addCase(getDocumentClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      .addCase(deleteDocumentClient.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteDocumentClient.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteDocumentClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      .addCase(getDocumentProperties.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.success = false;
      })
      .addCase(getDocumentProperties.fulfilled, (state, action) => {

        state.loading = false;
        state.documentProperties = action.payload || "Property not found.";
        state.pagination = action.payload.pagination || []
      })
      .addCase(getDocumentProperties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      .addCase(deleteDocumentProperty.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteDocumentProperty.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteDocumentProperty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      .addCase(getAdvisorProperties.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdvisorProperties.fulfilled, (state, action) => {
        state.loading = false;
        state.advisorProperties = action.payload;
      })
      .addCase(getAdvisorProperties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getAdvisorPropteryClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdvisorPropteryClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clientDetail = action.payload;
      })
      .addCase(getAdvisorPropteryClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getPropertiesofClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPropertiesofClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clientProperties = action.payload;
      })
      .addCase(getPropertiesofClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })
    // .addCase(getSecurities.pending, (state) => {
    //   state.loading = true;
    //   state.error = null;
    // })
    // .addCase(getSecurities.fulfilled, (state, action) => {
    //   state.loading = false;
    //   state.securities = action.payload;
    // })
    // .addCase(getSecurities.rejected, (state, action) => {
    //   state.loading = false;
    //   state.error = action.payload;
    // });
    builder
      .addCase(getInvestments.pending, (state) => {
        state.loadingInvestments = true;
        state.errorInvestments = null;
      })
      .addCase(getInvestments.fulfilled, (state, action) => {
        state.loadingInvestments = false;
        state.investments = action.payload || [];
      })
      .addCase(getInvestments.rejected, (state, action) => {
        state.loadingInvestments = false;
        state.errorInvestments =
          action.payload || "Failed to fetch investments";
      })
    builder
      .addCase(getDocumentsByAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.clientSecurityPagination = {};
      })
      .addCase(getDocumentsByAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload?.data;
        state.clientSecurityPagination = action.payload?.pagination;
      })
      .addCase(getDocumentsByAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.clientSecurityPagination = {};
      });
    builder
      .addCase(getDocumentsByAccountType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDocumentsByAccountType.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload?.data || [];
        state.docViewPagination = action.payload?.pagination;
      })
      .addCase(getDocumentsByAccountType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.docViewPagination = {}
      });
    ;
  },
});

export default documentSlice.reducer;
